import { BACKRS_REGION_OPTIONS } from '@backrs_ui/common-ui/src/models/college';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import {
  SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_SCHOLARSHIPS,
  SEARCH_CATEGORIES_INTERNSHIPS,
  SEARCH_CATEGORIES_COLLEGES,
  SEARCH_CATEGORIES_EMPLOYERS,
} from './search.filters';
import { SearchParentCategory, SearchCategory } from './search.types';

const TRACK_LABELS = appStrings.common.careerTrackShortNames;

export const SEARCH_PARENT_CATEGORIES_DEFAULT: Record<string, SearchParentCategory> = {
  PLAYLISTS: {
    name: 'Playbooks',
    iconName: 'ClipboardPlus',
    categories: [{ key: SEARCH_CATEGORIES.PLAYLISTS, label: 'Playbooks' }],
    isInMoreFilters: false,
  },
  CAREER_OPPORTUNITIES: {
    name: 'Opportunities',
    iconName: 'Luggage02',
    categories: [
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS, label: 'Internships' },
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS, label: 'Jobs' },
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_CAREER_PATH, label: 'Career Paths' },
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_EMPLOYERS, label: 'Employers' },
    ],
    isInMoreFilters: false,
  },
  SCHOLARSHIPS: {
    name: 'Scholarships',
    iconName: 'Calendar',
    categories: [
      { key: SEARCH_CATEGORIES.SCHOLARSHIPS_SCHOLARSHIPS, label: 'Scholarships' },
      { key: SEARCH_CATEGORIES.SCHOLARSHIPS_FINANCIAL_AID_COMPETENCY, label: 'Financial Aid' },
    ],
    isInMoreFilters: false,
  },
  TOOLS: {
    name: 'Tools',
    iconName: 'Users01',
    categories: [{ key: SEARCH_CATEGORIES.TOOLS, label: 'Backrs Tools' }],
    isInMoreFilters: true,
  },
  NUGGET_STORIES: {
    name: 'Stories',
    iconName: 'Film03',
    categories: [{ key: SEARCH_CATEGORIES.NUGGET_STORIES, label: 'Stories' }],
    isInMoreFilters: true,
  },
};

export const SEARCH_PARENT_CATEGORIES_SCHOLARSHIPS: Record<string, SearchParentCategory> = {
  TYPES: {
    name: 'Types',
    iconName: 'Calendar',
    categories: [
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_ALL, label: 'All', clearsOtherFilters: true },
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_SCHOLARSHIPS, label: 'Scholarships' },
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_FORGIVABLE_LOAN, label: 'Forgivable Loans' },
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_GRANT, label: 'Grants' },
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_CORPORATE, label: 'Corporate' },
      { key: SEARCH_CATEGORIES_SCHOLARSHIPS.TYPE_FELLOWSHIP, label: 'Fellowships' },
    ],
    isInMoreFilters: false,
  },
};

export const SEARCH_PARENT_CATEGORIES_INTERNSHIPS: Record<string, SearchParentCategory> = {
  GEOGRAPHY: {
    name: 'Geography',
    iconName: 'Map01',
    categories: [
      { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ALL, label: 'All', clearsOtherFilters: true },
      { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_REMOTE, label: 'Remote' },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_AL,
        label: 'Alabama',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BIRMINGHAM, label: 'Birmingham' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_HUNTSVILLE, label: 'Huntsville' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_AZ,
        label: 'Arizona',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MESA, label: 'Mesa' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PHOENIX, label: 'Phoenix' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SCOTTSDALE, label: 'Scottsdale' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TEMPE, label: 'Tempe' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TUCSON, label: 'Tucson' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_AR,
        label: 'Arkansas',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LITTLE_ROCK, label: 'Little Rock' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_CA,
        label: 'California',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BAKERSFIELD, label: 'Bakersfield' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_FRESNO, label: 'Fresno' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LOS_ANGELES, label: 'Los Angeles' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MODESTO, label: 'Modesto' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_RIVERSIDE, label: 'Riverside' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SACRAMENTO, label: 'Sacramento' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SAN_DIEGO, label: 'San Diego' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SAN_FRANCISCO, label: 'San Francisco' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SAN_JOSE, label: 'San Jose' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STOCKTON, label: 'Stockton' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_CO,
        label: 'Colorado',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BOULDER, label: 'Boulder' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_COLORADO_SPRINGS, label: 'Colorado Springs' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DENVER, label: 'Denver' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_FORT_COLLINS, label: 'Fort Collins' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_CT,
        label: 'Connecticut',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BRIDGEPORT, label: 'Bridgeport' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_HARTFORD, label: 'Hartford' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_NEW_HAVEN, label: 'New Haven' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_DC,
        label: 'District of Columbia',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_WASHINGTON, label: 'Washington, DC' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_FL,
        label: 'Florida',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_JACKSONVILLE, label: 'Jacksonville' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MIAMI, label: 'Miami' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ORLANDO, label: 'Orlando' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TAMPA, label: 'Tampa' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_GA,
        label: 'Georgia',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ATLANTA, label: 'Atlanta' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SAVANNAH, label: 'Savannah' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_ID,
        label: 'Idaho',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BOISE, label: 'Boise' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_IL,
        label: 'Illinois',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CHICAGO, label: 'Chicago' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_IN,
        label: 'Indiana',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_INDIANAPOLIS, label: 'Indianapolis' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_IA,
        label: 'Iowa',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DES_MOINES, label: 'Des Moines' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_KS,
        label: 'Kansas',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_WICHITA, label: 'Wichita' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_KY,
        label: 'Kentucky',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LEXINGTON, label: 'Lexington' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LOUISVILLE, label: 'Louisville' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_LA,
        label: 'Louisiana',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BATON_ROUGE, label: 'Baton Rouge' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_NEW_ORLEANS, label: 'New Orleans' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_MD,
        label: 'Maryland',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BALTIMORE, label: 'Baltimore' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_MA,
        label: 'Massachusetts',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BOSTON, label: 'Boston' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_WORCESTER, label: 'Worcester' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_MI,
        label: 'Michigan',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ANN_ARBOR, label: 'Ann Arbor' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DETROIT, label: 'Detroit' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_GRAND_RAPIDS, label: 'Grand Rapids' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LANSING, label: 'Lansing' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_MN,
        label: 'Minnesota',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MINNEAPOLIS, label: 'Minneapolis' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_MO,
        label: 'Missouri',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_KANSAS_CITY, label: 'Kansas City' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ST_LOUIS, label: 'St. Louis' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NE,
        label: 'Nebraska',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_OMAHA, label: 'Omaha' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NV,
        label: 'Nevada',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LAS_VEGAS, label: 'Las Vegas' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_RENO, label: 'Reno' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NJ,
        label: 'New Jersey',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CAMDEN, label: 'Camden' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_NEWARK, label: 'Newark' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NM,
        label: 'New Mexico',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ALBUQUERQUE, label: 'Albuquerque' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NY,
        label: 'New York',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ALBANY, label: 'Albany' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BUFFALO, label: 'Buffalo' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_NEW_YORK, label: 'New York' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ROCHESTER, label: 'Rochester' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SYRACUSE, label: 'Syracuse' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_NC,
        label: 'North Carolina',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CHARLOTTE, label: 'Charlotte' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DURHAM, label: 'Durham' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_GREENSBORO, label: 'Greensboro' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_RALEIGH, label: 'Raleigh' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_OH,
        label: 'Ohio',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_AKRON, label: 'Akron' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CINCINNATI, label: 'Cincinnati' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CLEVELAND, label: 'Cleveland' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_COLUMBUS, label: 'Columbus' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DAYTON, label: 'Dayton' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TOLEDO, label: 'Toledo' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_OK,
        label: 'Oklahoma',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_OKLAHOMA_CITY, label: 'Oklahoma City' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TULSA, label: 'Tulsa' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_OR,
        label: 'Oregon',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_EUGENE, label: 'Eugene' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PORTLAND, label: 'Portland' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_PA,
        label: 'Pennsylvania',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_ALLENTOWN, label: 'Allentown' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_HARRISBURG, label: 'Harrisburg' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PHILADELPHIA, label: 'Philadelphia' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PITTSBURGH, label: 'Pittsburgh' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_RI,
        label: 'Rhode Island',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PROVIDENCE, label: 'Providence' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_SC,
        label: 'South Carolina',
        isParent: true,
        subCategories: [{ key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_GREENVILLE, label: 'Greenville' }],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_TN,
        label: 'Tennessee',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_CHATTANOOGA, label: 'Chattanooga' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_KNOXVILLE, label: 'Knoxville' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MEMPHIS, label: 'Memphis' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_NASHVILLE, label: 'Nashville' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_TX,
        label: 'Texas',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_AUSTIN, label: 'Austin' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_DALLAS, label: 'Dallas' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_EL_PASO, label: 'El Paso' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_HOUSTON, label: 'Houston' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_IRVING, label: 'Irving' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_LUBBOCK, label: 'Lubbock' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PLANO, label: 'Plano' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SAN_ANTONIO, label: 'San Antonio' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_UT,
        label: 'Utah',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_PROVO, label: 'Provo' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SALT_LAKE_CITY, label: 'Salt Lake City' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_VA,
        label: 'Virginia',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_RICHMOND, label: 'Richmond' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_VIRGINIA_BEACH, label: 'Virginia Beach' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_WA,
        label: 'Washington',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BELLEVUE, label: 'Bellevue' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BELLINGHAM, label: 'Bellingham' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_BREMERTON, label: 'Bremerton' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_EVERETT, label: 'Everett' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_KENNEWICK, label: 'Kennewick' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_KIRKLAND, label: 'Kirkland' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_OLYMPIA, label: 'Olympia' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_REDMOND, label: 'Redmond' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_RENTON, label: 'Renton' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SEATTLE, label: 'Seattle' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_SPOKANE, label: 'Spokane' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_TACOMA, label: 'Tacoma' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_VANCOUVER, label: 'Vancouver' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_YAKIMA, label: 'Yakima' },
        ],
      },
      {
        key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_STATE_WI,
        label: 'Wisconsin',
        isParent: true,
        subCategories: [
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MADISON, label: 'Madison' },
          { key: SEARCH_CATEGORIES_INTERNSHIPS.MSA_MILWAUKEE, label: 'Milwaukee' },
        ],
      },
    ],
    isInMoreFilters: false,
  },
  JOB_TYPE: {
    name: 'Job Type',
    iconName: 'Clipboard',
    categories: [
      { key: SEARCH_CATEGORIES_INTERNSHIPS.JOB_TYPE_ALL, label: 'All', clearsOtherFilters: true },
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS, label: 'Internships' },
      { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS, label: 'Jobs' },
    ],
    isInMoreFilters: false,
  },
};

export const SEARCH_PARENT_CATEGORIES_COLLEGES: Record<string, SearchParentCategory> = {
  TYPE: {
    name: appStrings.modules.collegeFilters.program.label,
    iconName: 'GraduationHat01',
    isInMoreFilters: true,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_TYPE_FOUR_YEAR,
        label: appStrings.modules.collegeFilters.program.options.four_year,
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_TYPE_TWO_YEAR,
        label: appStrings.modules.collegeFilters.program.options.community_college,
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_TYPE_CERTIFICATE,
        label: appStrings.modules.collegeFilters.program.options.trade_school,
      },
    ],
  },
  CONTROL: {
    name: appStrings.modules.collegeFilters.schoolType.label,
    iconName: 'Building01',
    isInMoreFilters: true,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_CONTROL_PUBLIC,
        label: appStrings.modules.collegeFilters.schoolType.options.public,
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_CONTROL_PRIVATE,
        label: appStrings.modules.collegeFilters.schoolType.options.private,
      },
    ],
  },
  FOCUS: {
    name: 'Focus',
    iconName: 'BookOpen01',
    isInMoreFilters: true,
    categories: [
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_FOCUS_RESEARCH, label: 'Research' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_FOCUS_LIBERAL_ARTS, label: 'Liberal Arts' },
    ],
  },
  SELECTIVITY: {
    name: 'Selectivity',
    iconName: 'Target04',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SELECTIVITY_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SELECTIVITY_HIGHLY_SELECTIVE,
        label: 'Highly Selective',
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SELECTIVITY_SOMEWHAT_SELECTIVE,
        label: 'Somewhat Selective',
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SELECTIVITY_LESS_SELECTIVE,
        label: 'Less Selective',
      },
    ],
  },
  SIZE: {
    name: 'Size',
    iconName: 'Users01',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SIZE_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SIZE_VERY_SMALL, label: 'Very Small' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SIZE_SMALL, label: 'Small' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SIZE_MEDIUM, label: 'Medium' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_SIZE_LARGE, label: 'Large' },
    ],
  },
  REGION: {
    name: 'Region',
    iconName: 'Globe01',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_REGION_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      ...Object.entries(BACKRS_REGION_OPTIONS).map(([key, value]) => ({
        key: `COLLEGE_REGION_${key}` as SearchCategory,
        label: value,
      })),
    ],
  },
  STATE: {
    name: 'State',
    iconName: 'Flag01',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ALABAMA, label: 'Alabama' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ALASKA, label: 'Alaska' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ARIZONA, label: 'Arizona' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ARKANSAS, label: 'Arkansas' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_CALIFORNIA, label: 'California' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_COLORADO, label: 'Colorado' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_CONNECTICUT, label: 'Connecticut' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_DELAWARE, label: 'Delaware' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_FLORIDA, label: 'Florida' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_GEORGIA, label: 'Georgia' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_HAWAII, label: 'Hawaii' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_IDAHO, label: 'Idaho' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_ILLINOIS, label: 'Illinois' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_INDIANA, label: 'Indiana' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_IOWA, label: 'Iowa' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_KANSAS, label: 'Kansas' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_KENTUCKY, label: 'Kentucky' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_LOUISIANA, label: 'Louisiana' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MAINE, label: 'Maine' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MARYLAND, label: 'Maryland' },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MASSACHUSETTS,
        label: 'Massachusetts',
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MICHIGAN, label: 'Michigan' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MINNESOTA, label: 'Minnesota' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MISSISSIPPI, label: 'Mississippi' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MISSOURI, label: 'Missouri' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_MONTANA, label: 'Montana' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEBRASKA, label: 'Nebraska' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEVADA, label: 'Nevada' },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEW_HAMPSHIRE,
        label: 'New Hampshire',
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEW_JERSEY, label: 'New Jersey' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEW_MEXICO, label: 'New Mexico' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NEW_YORK, label: 'New York' },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NORTH_CAROLINA,
        label: 'North Carolina',
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_NORTH_DAKOTA, label: 'North Dakota' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_OHIO, label: 'Ohio' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_OKLAHOMA, label: 'Oklahoma' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_OREGON, label: 'Oregon' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_PENNSYLVANIA, label: 'Pennsylvania' },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_RHODE_ISLAND,
        label: 'Rhode Island',
      },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_SOUTH_CAROLINA,
        label: 'South Carolina',
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_SOUTH_DAKOTA, label: 'South Dakota' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_TENNESSEE, label: 'Tennessee' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_TEXAS, label: 'Texas' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_UTAH, label: 'Utah' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_VERMONT, label: 'Vermont' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_VIRGINIA, label: 'Virginia' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_WASHINGTON, label: 'Washington' },
      {
        key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_WEST_VIRGINIA,
        label: 'West Virginia',
      },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_WISCONSIN, label: 'Wisconsin' },
      { key: SEARCH_CATEGORIES_COLLEGES.COLLEGE_STATE_WYOMING, label: 'Wyoming' },
    ],
  },
};

export const SEARCH_PARENT_CATEGORIES_EMPLOYERS: Record<string, SearchParentCategory> = {
  SIZE: {
    name: 'Size',
    iconName: 'Users01',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_SIZE_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      { key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_SIZE_SMALL, label: 'Small' },
      { key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_SIZE_MEDIUM, label: 'Medium' },
      { key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_SIZE_LARGE, label: 'Large' },
    ],
  },
  TRACK: {
    name: 'Track',
    iconName: 'Luggage02',
    isInMoreFilters: false,
    categories: [
      {
        key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_ALL,
        label: 'All',
        clearsOtherFilters: true,
      },
      {
        key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_SEMI_SKILLED,
        label: TRACK_LABELS['semi-skilled'],
      },
      { key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_TRADES, label: TRACK_LABELS.trades },
      {
        key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_MIDDLE_TIER,
        label: TRACK_LABELS['middle-tier'],
      },
      {
        key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_UPPER_TIER,
        label: TRACK_LABELS['upper-tier'],
      },
      { key: SEARCH_CATEGORIES_EMPLOYERS.EMPLOYER_TRACK_TOP_TIER, label: TRACK_LABELS['top-tier'] },
    ],
  },
};
